import React from "react";
import { PricingCard } from "../components/PricingCard";
import { ContactForm } from "../components/ContactForm";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PACKAGES = {
  "3_month": {
    title: "3ヶ月コンサルプラン",
    price: 300000,
    description: "知りたい事だけに焦点を当てて学びたい人におすすめ",
    priceSubtext: "eBayを副業として取り組みたい方におすすめ",
    features: [
      "月2回まで 1回60分 オンラインセッション",
      "チャットサポート（いつでも対応）",
      "SellerPlus 3ヶ月無料"
    ]
  },
  "6_month": {
    title: "6ヶ月コンサルプラン",
    price: 500000,
    description: "設定からリサーチイーベイの全てをしっかり学びたい方向け",
    priceSubtext: "ebayを将来的に生活の軸にしたい ebayを事業の柱にしたい方におすすめ",
    features: [
      "月6回まで 1回60分 オンラインセッション",
      "チャットサポート（いつでも対応）",
      "SellerPlus 6ヶ月無料"
    ]
  }
};

export default function HomePage() {
  const handleSelectPlan = async (packageId) => {
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/checkout/session`, {
        package_id: packageId,
        origin_url: originUrl
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error("決済ページへの遷移に失敗しました");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("エラーが発生しました。もう一度お試しください。");
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className="relative py-20 md:py-32 px-6 md:px-12 bg-gradient-to-b from-white to-slate-50 overflow-hidden"
      >
        {/* Background Image with overlay */}
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1761461535556-9bec8112eaab?crop=entropy&cs=srgb&fm=jpg&q=85')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        
        <div className="relative max-w-5xl mx-auto text-center space-y-8">
          <h1 
            data-testid="hero-title"
            className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-primary"
          >
            eBay販売を成功に導く
            <br />
            プロフェッショナルコンサルティング
          </h1>
          <p 
            data-testid="hero-subtitle"
            className="text-base md:text-lg leading-relaxed text-slate-600 max-w-2xl mx-auto"
          >
            新規セラーから既存セラーまで、経験豊富なコンサルタントがあなたのビジネスを全力でサポートします
          </p>
        </div>
      </section>

      {/* SellerPlus Tool Section */}
      <section 
        data-testid="sellerplus-section"
        className="py-20 md:py-32 px-6 md:px-12 bg-white border-b border-slate-200"
      >
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <div className="space-y-6">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-primary">
              これまでのEbay経験から培った<br />使えるツールと一緒にサポートします！
            </h2>
            
            <div className="space-y-4 text-base leading-relaxed text-slate-600 max-w-3xl mx-auto">
              <p>
                私が監修したツール<span className="font-semibold text-amber-600">《セラープラス》</span>は日々の運営を楽にし、作業時間を減らすこと、そしてEbayにおいて必要な競合分析機能を盛り込んでいます。
              </p>
              <p className="text-lg font-semibold text-primary">
                セラープラス × 実務型コンサルで運営基盤を一緒に作りましょう！！
              </p>
            </div>
          </div>

          <div className="pt-6">
            <a
              href="https://sellerplus.jp/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="sellerplus-link"
              className="inline-flex items-center gap-2 rounded-full bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 text-base font-medium transition-all duration-200 hover:scale-[1.02] shadow-[0_4px_12px_rgba(217,119,6,0.3)]"
            >
              セラープラスについて詳しく見る
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </a>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section 
        data-testid="pricing-section"
        className="py-20 md:py-32 px-6 md:px-12 bg-white"
      >
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <h2 
              data-testid="pricing-title"
              className="text-3xl md:text-4xl font-bold tracking-tight text-primary"
            >
              料金プラン
            </h2>
            <p className="text-base leading-relaxed text-slate-600">
              目標と予算に合わせて最適なプランをお選びください
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <PricingCard
              packageId="3_month"
              title={PACKAGES["3_month"].title}
              price={PACKAGES["3_month"].price}
              description={PACKAGES["3_month"].description}
              priceSubtext={PACKAGES["3_month"].priceSubtext}
              features={PACKAGES["3_month"].features}
              onSelect={handleSelectPlan}
            />
            <PricingCard
              packageId="6_month"
              title={PACKAGES["6_month"].title}
              price={PACKAGES["6_month"].price}
              description={PACKAGES["6_month"].description}
              priceSubtext={PACKAGES["6_month"].priceSubtext}
              features={PACKAGES["6_month"].features}
              isPopular={true}
              onSelect={handleSelectPlan}
            />
          </div>

          <div 
            data-testid="pricing-note"
            className="text-center text-sm text-slate-600 max-w-3xl mx-auto bg-slate-50 rounded-lg p-6"
          >
            <p>
              コンサル期間中のSellerPlus利用料はコンサル側が負担します。
              <br />
              期間終了後は希望者のみ継続利用となります。
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <ContactForm />

      {/* Footer */}
      <footer 
        data-testid="footer"
        className="py-8 px-6 bg-primary text-primary-foreground text-center"
      >
        <p className="text-sm">
          © 2025 eBay Consulting Service. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
